import React, { useCallback, useEffect, useRef, useState } from "react";
import { Header, Segment, Checkbox, Button, Divider, Grid } from "semantic-ui-react";
import { toast } from "react-toastify";
import {
  functionGetManagerStrategyCreditsOverview,
  functionUpdateManagerSharingSettings,
} from "../../../app/firestore/functionsService";
import ManagerStrategyCreditsSection from "../../payments/ManagerStrategyCreditsSection";

// Manager Strategy Credits (spec sections 39-46/78). Personal credits and
// the Manager-sponsored SHARED pool are always kept separate (never merged)
// - sharing/allow-list here only governs whether a CURRENT subordinate may
// draw from the pool; the Manager may always spend their own pool credits on
// any transaction they can access, regardless of this toggle.
const ROLE_GROUPS = [
  { role: "agent", label: "Agents" },
  { role: "assistant", label: "Assistants" },
  { role: "managerassistant", label: "Manager Assistants" },
];

export default function ManagerStrategyCreditsDashboard() {
  const [overview, setOverview] = useState(null);
  const [sharingEnabled, setSharingEnabled] = useState(false);
  const [allowedUserIds, setAllowedUserIds] = useState([]);
  const [saving, setSaving] = useState(false);
  // Guards every setState below an await - fetchOverview is called both on
  // mount AND later as ManagerStrategyCreditsSection's onPurchaseFulfilled
  // callback (after a Stripe purchase webhook lands), which can resolve
  // after the user has already navigated away from this page.
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchOverview = useCallback(() => {
    return functionGetManagerStrategyCreditsOverview()
      .then((data) => {
        if (!isMountedRef.current) return;
        setOverview(data);
        setSharingEnabled(data.sharingEnabled);
        setAllowedUserIds(data.allowedUserIds || []);
      })
      .catch((err) => {
        if (isMountedRef.current) toast.error(err.message);
      });
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  function toggleUser(userId) {
    setAllowedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  }

  function selectAllInGroup(role, select) {
    const ids = (overview?.roster || []).filter((u) => u.role === role).map((u) => u.id);
    setAllowedUserIds((prev) => (select ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const result = await functionUpdateManagerSharingSettings({ sharingEnabled, allowedUserIds });
      if (!isMountedRef.current) return;
      setAllowedUserIds(result.allowedUserIds);
      toast.success("Manager Strategy Credit sharing settings saved.");
    } catch (err) {
      if (isMountedRef.current) toast.error(err.message);
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }

  if (!overview) return null;

  return (
    <div className="secondary-page-wrapper">
      <Header size="huge" color="blue">
        Manager Strategy Credits
      </Header>

      <Segment>
        <Header as="h3">Personal</Header>
        <p>
          Free: {overview.personalCredits.freeComped}
          <br />
          Paid: {overview.personalCredits.paid}
        </p>
      </Segment>

      <Segment>
        <Header as="h3">Manager-Sponsored Shared</Header>
        <p>Shared Paid Credits: {overview.sharedPoolCredits.total} available</p>

        <ManagerStrategyCreditsSection onPurchaseFulfilled={fetchOverview} />

        <Checkbox
          toggle
          label="Share Manager Strategy Credits"
          checked={sharingEnabled}
          onChange={(e, { checked }) => setSharingEnabled(checked)}
        />

        <Divider />
        <Header as="h4">Who may use them?</Header>
        <p className="text-grey small bottom margin">
          Only Agents, Assistants, and Manager Assistants CURRENTLY under your management appear here.
        </p>
        <Grid stackable columns={3}>
          {ROLE_GROUPS.map((group) => {
            const usersInGroup = (overview.roster || []).filter((u) => u.role === group.role);
            return (
              <Grid.Column key={group.role}>
                <Header as="h5">{group.label}</Header>
                <div className="small bottom margin">
                  <Button size="mini" basic onClick={() => selectAllInGroup(group.role, true)}>
                    Select All
                  </Button>
                  <Button size="mini" basic onClick={() => selectAllInGroup(group.role, false)}>
                    Select None
                  </Button>
                </div>
                {usersInGroup.length === 0 && <p className="text-grey">None</p>}
                {usersInGroup.map((u) => (
                  <div key={u.id} className="small bottom margin">
                    <Checkbox
                      label={`${u.firstName} ${u.lastName}`}
                      checked={allowedUserIds.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                    />
                  </div>
                ))}
              </Grid.Column>
            );
          })}
        </Grid>

        <Divider />
        <Button primary loading={saving} disabled={saving} onClick={handleSave}>
          Save
        </Button>
      </Segment>
    </div>
  );
}
